import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, ReplaySubject, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

export type UserType = 'internal' | 'client';
export type ClientPermission = 'faturas' | 'marcas' | 'perfil' | 'documentos' | 'certificacoes';

export interface User {
  _id: string;
  nome: string;
  email: string;
  role: any;
  userType?: UserType;
  clientId?: string;
  clientPermissions?: ClientPermission[];
  preferencias: {
    tema: 'dark' | 'light';
    idioma: 'pt-br' | 'en';
  };
  mfaEnabled: boolean;
}

export interface AuthResponse {
  success: boolean;
  data: {
    user: User;
    accessToken: string;
    refreshToken?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private accessToken: string | null = null;

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();

  private authReadySubject = new ReplaySubject<boolean>(1);
  public authReady$ = this.authReadySubject.asObservable();

  private apiUrl = `${environment.backendUrl}/auth`;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Defer HTTP call until after constructor completes — avoids circular DI
    // (interceptors inject AuthService, so calling HttpClient mid-construction throws NG0200).
    queueMicrotask(() => this.checkAuth());
  }

  private checkAuth(): void {
    // Restore user display info from localStorage (no token stored there)
    const storedUser = this.getStoredUser();
    if (storedUser) {
      this.currentUserSubject.next(storedUser);
    }

    // Silently try to get a fresh access token from the httpOnly refresh cookie
    this.http
      .post<AuthResponse>(`${this.apiUrl}/refresh-token`, {}, { withCredentials: true })
      .subscribe({
        next: (res) => {
          if (res.data?.accessToken) {
            this.accessToken = res.data.accessToken;
            const user = res.data.user;
            this.currentUserSubject.next(user);
            this.isLoggedInSubject.next(true);
            localStorage.setItem('currentUser', JSON.stringify(user));
          }
          this.authReadySubject.next(true);
        },
        error: () => {
          // Do NOT clear localStorage here — the guard redirects to /login when accessToken is null.
          // Removing currentUser on a transient refresh failure causes unnecessary data loss.
          if (!this.accessToken) {
            this.currentUserSubject.next(null);
            this.isLoggedInSubject.next(false);
          }
          this.authReadySubject.next(true);
        },
      });
  }

  login(email: string, senha: string): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/login`, { email, senha })
      .pipe(
        tap((res) => {
          if (res.data?.mfaPending) {
            this.setToken(null);
            this.isLoggedInSubject.next(false);
            return;
          }

          const token = res.data?.accessToken ?? res.access_token ?? null;
          const user = res.data?.user ?? res.user ?? null;
          if (token && user) {
            this.setToken(token);
            localStorage.setItem('currentUser', JSON.stringify(user));
            localStorage.setItem('lastActivity', Date.now().toString());
            this.currentUserSubject.next(user);
            this.isLoggedInSubject.next(true);
          }
        })
      );
  }

  register(nome: string, email: string, senha: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.apiUrl}/register`, { nome, email, senha })
      .pipe(
        tap((res) => {
          this.setToken(res.data.accessToken);
          localStorage.setItem('currentUser', JSON.stringify(res.data.user));
          this.currentUserSubject.next(res.data.user);
          this.isLoggedInSubject.next(true);
        })
      );
  }

  refreshToken(): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.apiUrl}/refresh-token`, {}, { withCredentials: true })
      .pipe(
        tap((res) => {
          this.setToken(res.data.accessToken);
          localStorage.setItem('currentUser', JSON.stringify(res.data.user));
          this.currentUserSubject.next(res.data.user);
        })
      );
  }

  logout(): void {
    // Clear local state first so interceptor won't retry the logout call itself
    this.setToken(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('lastActivity');
    this.currentUserSubject.next(null);
    this.isLoggedInSubject.next(false);
    // Fire-and-forget — server-side refresh token invalidation (won't loop since token is already gone)
    this.http.post(`${this.apiUrl}/logout`, {}, { withCredentials: true }).subscribe({ error: () => {} });
    this.router.navigate(['/login']);
  }

  getProfile(): Observable<any> {
    return this.http.get(`${this.apiUrl}/profile`);
  }

  updateProfile(data: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/profile`, data).pipe(
      tap((res: any) => {
        if (res.success) {
          localStorage.setItem('currentUser', JSON.stringify(res.data));
          this.currentUserSubject.next(res.data);
        }
      })
    );
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  setToken(token: string | null): void {
    this.accessToken = token;
  }

  completeLogin(accessToken: string, user: User): void {
    this.accessToken = accessToken;
    this.currentUserSubject.next(user);
    this.isLoggedInSubject.next(true);
    localStorage.setItem('currentUser', JSON.stringify(user));
    localStorage.setItem('lastActivity', Date.now().toString());
  }

  updateCurrentUser(user: User): void {
    this.currentUserSubject.next(user);
    localStorage.setItem('currentUser', JSON.stringify(user));
  }

  getToken(): string | null {
    return this.accessToken;
  }

  isLoggedIn(): boolean {
    return !!this.accessToken && !!this.getCurrentUser();
  }

  isClientUser(): boolean {
    return this.getCurrentUser()?.userType === 'client';
  }

  // Papel Admin (fonte única de verdade no front, espelhando o helper
  // `isUserAdmin` do backend em auth.middleware). Usado por gates que o
  // sistema de permissões recurso:acao não expressa — ex.: remover pagamento
  // de fatura totalmente paga é restrito a Admin no backend.
  isAdmin(): boolean {
    const roleName = (this.getCurrentUser()?.role?.nome ?? '').toLowerCase();
    return ['admin', 'administrador', 'administrator'].includes(roleName);
  }

  hasClientPermission(module: ClientPermission): boolean {
    const user = this.getCurrentUser();
    if (!user || user.userType !== 'client') return false;
    return user.clientPermissions?.includes(module) ?? false;
  }

  getPortalHomeRoute(): string {
    return '/portal/dashboard';
  }

  hasPermission(recurso: string, acao?: string): boolean {
    const user = this.getCurrentUser();
    if (!user || !user.role || !user.role.permissoes) return false;

    const permissao = user.role.permissoes.find(
      (p: any) => p.recurso === recurso
    );
    if (!permissao) return false;
    if (!acao) return true;
    return permissao.acoes.includes(acao);
  }

  // Verifica uma chave canônica achatada (ex.: 'client:brand:tab:ngx').
  // O backend guarda `recurso` e `acao` separados, mas o `recurso` pode conter
  // ':' (ex.: recurso 'client:brand', acao 'tab:ngx'). Em vez de adivinhar onde
  // dividir a string, reconstruímos `${recurso}:${acao}` de cada permissão e
  // comparamos com a chave — funciona para qualquer quantidade de segmentos e
  // é compatível com as chaves de 2 segmentos existentes ('faturas:editar').
  hasPermissionKey(fullKey: string): boolean {
    const user = this.getCurrentUser();
    if (!user || !user.role || !user.role.permissoes) return false;

    return user.role.permissoes.some((p: any) => {
      if (p.recurso === fullKey) return true;
      return (p.acoes || []).some((acao: string) => `${p.recurso}:${acao}` === fullKey);
    });
  }

  getHomeRoute(): string {
    if (this.isClientUser()) return this.getPortalHomeRoute();

    const candidates = [
      { route: '/dashboard',        recurso: 'dashboard',        acao: 'visualizar' },
      { route: '/transactions',     recurso: 'lancamentos',      acao: 'visualizar' },
      { route: '/invoices',         recurso: 'faturas',          acao: 'visualizar' },
      { route: '/accounts-payable', recurso: 'contas-a-pagar',   acao: 'visualizar' },
      { route: '/employees',        recurso: 'funcionarios',     acao: 'visualizar' },
      { route: '/collaborators',    recurso: 'service-providers', acao: 'visualizar' },
      { route: '/clients',          recurso: 'clientes',         acao: 'visualizar' },
      { route: '/categories',       recurso: 'categorias',       acao: 'visualizar' },
    ];
    for (const c of candidates) {
      if (this.hasPermission(c.recurso, c.acao)) return c.route;
    }
    return '/login';
  }

  changePassword(senhaAtual: string, novaSenha: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/change-password`, { senhaAtual, novaSenha });
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/forgot-password`, { email });
  }

  resetPassword(token: string, novaSenha: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/reset-password`, { token, novaSenha });
  }

  private getStoredUser(): User | null {
    const stored = localStorage.getItem('currentUser');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  }
}
